<template>
  <div class="mx-4">
    <v-card class="mb-4 shadow-sm">
      <v-card-title class="headline d-flex align-center py-3 px-4 grey lighten-5">
        <v-btn icon @click="$router.back()" class="mr-3" color="primary">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="font-weight-bold">Cash Budget Preview</span>
        <v-spacer></v-spacer>
        <v-btn v-if="$rbac.can('report.export')" color="success" @click="exportExcel" elevation="1">
          <v-icon left>mdi-file-excel</v-icon>
          Export Excel
        </v-btn>
      </v-card-title>
    </v-card>

    <div v-if="data && data.months && data.months.length > 0">
      <v-row dense>
        <!-- PART 1: Penerimaan dan Pengeluaran -->
        <v-col cols="12" class="mb-4">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2"
              >Penerimaan dan Pengeluaran</v-card-title
            >
            <v-simple-table dense class="cash-budget-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th
                      rowspan="2"
                      class="sticky-col first-col grey lighten-5"
                      style="border: 1px solid #bdbdbd !important;"
                    >
                      Keterangan
                    </th>
                    <th
                      v-for="(m, i) in data.months"
                      :key="'m' + i"
                      :colspan="m.colSpan"
                      :rowspan="m.summary ? 2 : 1"
                      class="text-center border-left text-uppercase grey lighten-5 align-middle"
                      style="border: 1px solid #bdbdbd !important;"
                    >
                      {{ m.month }} {{ m.year }}
                    </th>
                    <th
                      rowspan="2"
                      class="text-center border-left font-weight-bold grey lighten-3 align-middle"
                      style="border: 1px solid #bdbdbd !important;"
                    >
                      TOTAL
                    </th>
                  </tr>
                  <tr>
                    <th
                      v-for="(w, i) in data.weeks"
                      :key="'w' + i"
                      class="text-center border-left small-text grey lighten-5"
                      style="border: 1px solid #bdbdbd !important;"
                    >
                      {{ formatDateSmall(w.start) }} s/d {{ formatDateSmall(w.end) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in part1Rows" :key="row.label" :class="row.class">
                    <td class="sticky-col first-col font-weight-medium">
                      {{ row.label }}
                    </td>
                    <td
                      v-for="(val, i) in data[row.key]"
                      :key="i"
                      class="text-right border-left"
                      :class="[
                        row.clickable ? 'clickable-cell' : '',
                        val.total < 0 ? 'red--text' : '',
                      ]"
                      @click="row.clickable ? openDetail(row, i) : null"
                    >
                      {{ formatPrice(val.total) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>

        <!-- PART 2: Bank Dan AR -->
        <v-col cols="12" class="mb-4">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2"
              >Bank Dan AR</v-card-title
            >
            <v-simple-table dense class="cash-budget-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th
                      rowspan="2"
                      class="sticky-col first-col grey lighten-5"
                      style="border: 1px solid #bdbdbd !important;"
                    >
                      Keterangan
                    </th>
                    <th
                      v-for="(m, i) in data.months"
                      :key="'m2' + i"
                      :colspan="m.colSpan"
                      :rowspan="m.summary ? 2 : 1"
                      class="text-center border-left text-uppercase grey lighten-5 align-middle"
                      style="border: 1px solid #bdbdbd !important;"
                    >
                      {{ m.month }} {{ m.year }}
                    </th>
                    <th
                      rowspan="2"
                      class="text-center border-left font-weight-bold grey lighten-3 align-middle"
                      style="border: 1px solid #bdbdbd !important;"
                    >
                      TOTAL
                    </th>
                  </tr>
                  <tr>
                    <th
                      v-for="(w, i) in data.weeks"
                      :key="'w2' + i"
                      class="text-center border-left small-text grey lighten-5"
                      style="border: 1px solid #bdbdbd !important;"
                    >
                      {{ formatDateSmall(w.start) }} s/d {{ formatDateSmall(w.end) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in part2Rows" :key="row.label" :class="row.class">
                    <td class="sticky-col first-col font-weight-medium">
                      {{ row.label }}
                    </td>
                    <td
                      v-for="(val, i) in data[row.key]"
                      :key="i"
                      class="text-right border-left"
                      :class="[
                        row.clickable ? 'clickable-cell' : '',
                        val.total < 0 ? 'red--text' : '',
                      ]"
                      @click="row.clickable ? openDetail(row, i) : null"
                    >
                      {{ formatPrice(val.total) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <div v-else class="text-center pa-10 mt-10">
      <v-alert type="info" text prominent icon="mdi-information-outline">
        Data preview tidak tersedia. Silakan lakukan generate dari halaman utama.
      </v-alert>
      <v-btn color="primary" x-large @click="$router.push('/report/cash-budget')" class="mt-4">
        Kembali ke Parameter
      </v-btn>
    </div>

    <!-- Detail Dialog -->
    <v-dialog v-model="dialogDetail" max-width="1200px" scrollable>
      <v-card>
        <v-card-title class="headline grey lighten-2 py-3 px-4">
          <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
          Rincian: {{ detailTitle }}
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogDetail = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-row v-if="detailSummary" class="mb-4 bg-light pa-2 rounded border mx-0">
            <v-col cols="12" sm="5">
              <div class="grey--text text--darken-1 caption text-uppercase">Item</div>
              <div class="subtitle-1 font-weight-bold primary--text">{{ detailSummary.label }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="grey--text text--darken-1 caption text-uppercase">Periode Minggu</div>
              <div class="subtitle-1 font-weight-bold">{{ detailSummary.period }}</div>
            </v-col>
            <v-col cols="12" sm="3" class="text-right">
              <div class="grey--text text--darken-1 caption text-uppercase">Total Nilai</div>
              <div
                class="subtitle-1 font-weight-bold"
                :class="detailSummary.total < 0 ? 'red--text' : ''"
              >
                {{ formatPrice(detailSummary.total) }}
              </div>
            </v-col>
          </v-row>

          <v-data-table
            :headers="detailHeaders"
            :items="detailItems"
            :loading="loadingDetail"
            :page.sync="detailPage"
            :items-per-page.sync="detailItemsPerPage"
            dense
            fixed-header
            height="500px"
            class="elevation-1 border"
            :no-data-text="'Tidak ada rincian data untuk periode ini'"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 100],
            }"
          >
            <template v-slot:footer.page-text="{ itemsLength }">
              Halaman {{ detailPage }} dari {{ Math.ceil(itemsLength / detailItemsPerPage) }}
            </template>
            <template v-slot:item.total="{ item }">
              <span :class="item.total < 0 ? 'red--text' : ''" class="font-weight-medium">
                {{ formatPrice(item.total) }}
              </span>
            </template>
            <template v-slot:item.date="{ item }">
              {{ item.date !== "-" ? formatDateFull(item.date) : "-" }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="py-2 px-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialogDetail = false">Tutup</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/services/api";
import moment from "moment";
import fileDownload from "js-file-download";

export default {
  name: "cash-budget-preview",
  data() {
    return {
      dialogDetail: false,
      loadingDetail: false,
      detailTitle: "",
      detailItems: [],
      detailSummary: null,
      detailPage: 1,
      detailItemsPerPage: 10,
      detailHeaders: [
        { text: "Tgl Transaksi", value: "date", align: "start", width: "120px" },
        { text: "No. Dokumen", value: "number", width: "250px" },
        { text: "Entity / Relasi", value: "entity", width: "300px" },
        { text: "No. PO", value: "poNumber", width: "200px" },
        { text: "Total (IDR)", value: "total", align: "right", width: "180px" },
      ],
      part2Rows: [
        { label: "Payment A/P Rp", key: "paymentApIdrs", clickable: true, detailCat: "AP_IDR" },
        {
          label: "Payment A/P Valas",
          key: "paymentApValas",
          clickable: true,
          detailCat: "AP_VALAS",
        },
        { label: "Gaji", key: "salaries" },
        { label: "PPH", key: "pph" },
        { label: "PPN", key: "ppn" },
        { label: "Telp, Listrik, IPL, Sewa Gdu, etc", key: "cost" },
        { label: "Hutang Shareholder", key: "shareholders" },
        { label: "BPJS-TK", key: "bpjsTk" },
        { label: "BPJS Kesehatan", key: "bpjsHealth" },
        { label: "Outsourcing", key: "outsourcing" },
        { label: "PIB", key: "pib" },
        { label: "Bunga Pinjaman", key: "interests" },
        { label: "Bank Garansi", key: "guarantees" },
        { label: "DO Outstanding Rp", key: "doOutstandingIdrs" },
        { label: "DO outstanding valas", key: "doOutstandingValas" },
        { label: "THR", key: "thr" },
        {
          label: "Outstanding PO IDR",
          key: "outstandingPoIdrs",
          clickable: true,
          detailCat: "PO_IDR",
        },
        {
          label: "Outstanding PO Valas",
          key: "outstandingPoValas",
          clickable: true,
          detailCat: "PO_VALAS",
        },
        { label: "Cicilan Mobil Alphard", key: "instalments" },
        { label: "Total", key: "totalCost", class: "orange lighten-5 font-weight-bold" },
      ],
    };
  },
  computed: {
    data() {
      return this.$store.state.report.currentCashBudget;
    },
    reportStartDate() {
      return this.$route.query.startDate || moment().format("YYYY-MM-DD");
    },
    part1Rows() {
      return [
        { label: "Fasilitas Bank", key: "facilityBanks" },
        { label: "Bank", key: "banks" },
        { label: "AR jatuh tempo", key: "ars", clickable: true, detailCat: "AR" },
        { label: "Unbilled Revenue", key: "unbilled", clickable: true, detailCat: "UNBILLED" },
        { label: "PO Backlog", key: "notReady", clickable: true, detailCat: "NOT_READY" },
        { label: "BPO", key: "bpo", clickable: true, detailCat: "BPO" },
        { label: "PO Problem", key: "problem", clickable: true, detailCat: "PROBLEM" },
        {
          label: "Total Perkiraan Penerimaan",
          key: "totalEstimation",
          class: "blue lighten-5 font-weight-bold",
        },
        {
          label: "Plan Invoice Unbilled+Backlog+BPO",
          key: "plan",
          clickable: true,
          detailCat: "PLAN",
        },
        {
          label: "Actual Sales & Revenue",
          key: "sales",
          clickable: true,
          detailCat: "SALES",
        },
        {
          label: "Sales & Revenue reverse yang Batal",
          key: "reverse",
          clickable: true,
          detailCat: "REVERSE",
        },
        {
          label: "Forecast Sales & Revenue",
          key: "totalPlanSales",
          clickable: true,
          detailCat: "SALES_PLAN",
        },
        { label: "PO", key: "po" },
        { label: "Hutang Bank", key: "debtBanks" },
        { label: "Hutang Amtel", key: "debtAmtels" },
        { label: "Hutang PWS", key: "debtPws" },
        { label: "Estimasi pengeluaran karyawan", key: "totalCostEmployee" },
        { label: "Perkiraan pengeluaran vendor", key: "totalCostVendor" },
        {
          label: "Total perkiraan pengeluaran",
          key: "totalExpense",
          class: "red lighten-5 font-weight-bold",
        },
        {
          label: "Total perkiraan saldo",
          key: "totalBalance",
          class: "green lighten-5 font-weight-bold",
        },
        { label: "Aktiva Lancar", key: "aktiva" },
        { label: "Kewajiban Lancar", key: "kewajiban" },
        { label: "NWC", key: "nwc" },
        { label: "Selisih", key: "selisih" },
        { label: "Uang muka penjualan", key: "uangMukaPenjualan" },
      ];
    },
  },
  methods: {
    formatPrice(value) {
      if (value === null || value === undefined) return "0";
      let val = (value / 1).toFixed(2).replace(".", ",");
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    formatDateSmall(date) {
      return moment(date).format("DD MMM");
    },
    formatDateFull(date) {
      return moment(date).format("DD MMM YYYY");
    },
    async openDetail(row, index) {
      const rowItems = this.data[row.key];
      const isTotalCol = index === rowItems.length - 1;
      const weekData = index < this.data.weeks.length ? this.data.weeks[index] : null;

      this.detailTitle = row.label;

      let periodText = "";
      if (isTotalCol) {
        periodText = "Seluruh Periode";
      } else if (weekData) {
        periodText = `${this.formatDateSmall(weekData.start)} s/d ${this.formatDateSmall(
          weekData.end
        )}`;
      } else {
        // Monthly summary column
        const monthIndex = index - this.data.weeks.length;
        const targetMonth = this.data.months.filter(m => m.summary)[monthIndex];
        periodText = targetMonth ? `${targetMonth.month} ${targetMonth.year}` : "Periode Terpilih";
      }

      this.detailSummary = {
        label: row.label,
        period: periodText,
        total: rowItems[index].total,
      };

      this.dialogDetail = true;
      this.loadingDetail = true;
      this.detailPage = 1;
      this.detailItems = [];

      let startDate = this.$route.query.startDate || moment().format("YYYY-MM-DD");
      let endDate = moment()
        .add(5, "years")
        .format("YYYY-MM-DD");

      const isCumulativeCat = !["SALES", "REVERSE", "SALES_PLAN"].includes(row.detailCat);

      if (isTotalCol) {
        if (isCumulativeCat) {
          startDate = "2010-01-01";
        } else {
          startDate = this.$route.query.startDate || moment().format("YYYY-MM-DD");
        }

        if (this.data.months && this.data.months.length > 0) {
          const lastMonth = this.data.months[this.data.months.length - 1];
          endDate = moment(`${lastMonth.year}-${lastMonth.monthNumber}-01`, "YYYY-M-DD")
            .endOf("month")
            .format("YYYY-MM-DD");
        }
      } else if (weekData) {
        startDate =
          index === 0 && isCumulativeCat
            ? "2010-01-01"
            : moment(weekData.start).format("YYYY-MM-DD");
        endDate = moment(weekData.end).format("YYYY-MM-DD");
      } else {
        const monthIndex = index - this.data.weeks.length;
        const targetMonths = this.data.months.filter(m => m.summary);
        if (targetMonths[monthIndex]) {
          const tm = targetMonths[monthIndex];
          startDate = moment(`${tm.year}-${tm.monthNumber}-01`, "YYYY-M-DD").format("YYYY-MM-DD");
          endDate = moment(startDate)
            .endOf("month")
            .format("YYYY-MM-DD");
        }
      }

      try {
        const res = await api.post("/reports/cash-budget/detail", {
          category: row.detailCat,
          startDate: startDate,
          endDate: endDate,
          reportStartDate: this.$route.query.startDate,
        });
        this.detailItems = res.data.map(x => ({
          date: x.invoiceDate || x.planInvoice || x.pengajuanDate || x.date || x.po_date || "-",
          number: x.invoiceNumber || x.po_number || x.projectNumber || x.number || "-",
          entity: x.vendorName || x.customerName || x.vendor_name || x.entity || "-",
          poNumber: x.poNumber || x.po_number || "-",
          total:
            x.total !== undefined
              ? x.total
              : x.balance !== undefined
              ? x.balance
              : x.lineBalance !== undefined
              ? x.lineBalance
              : 0,
        }));
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal memuat detail" });
      } finally {
        this.loadingDetail = false;
      }
    },
    async exportExcel() {
      this.$store.commit("SET_LOADER", true);
      try {
        const body = {
          startDate: this.$route.query.startDate,
          endDate: this.$route.query.endDate,
          month: parseInt(this.$route.query.month),
          items: this.$store.state.report.cashBudgetItems || [],
        };
        const response = await api.post("/reports/cash-budget/v2/excel", body, {
          responseType: "blob",
        });
        fileDownload(response.data, `report-cash-budget-${body.startDate}.xlsx`);
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal mengunduh Excel" });
      } finally {
        this.$store.commit("SET_LOADER", false);
      }
    },
  },
};
</script>

<style scoped>
.cash-budget-table {
  border: 1px solid #bdbdbd;
  margin-top: 10px;
}
.cash-budget-table table {
  border-collapse: collapse !important;
}
.cash-budget-table ::v-deep th {
  border: 1px solid #9e9e9e !important;
}
.sticky-col {
  position: sticky;
  left: 0;
  background: white !important;
  z-index: 2;
  border-right: 2px solid #bdbdbd !important;
}
.first-col {
  min-width: 250px;
}
.border-left {
  border-left: 1px solid #e0e0e0 !important;
}
.border-bottom {
  border-bottom: 1px solid #e0e0e0 !important;
}
.border-top {
  border-top: 1px solid #e0e0e0 !important;
}
.clickable-cell {
  cursor: pointer;
  color: #1976d2;
  text-decoration: underline;
  font-weight: 500;
}
.clickable-cell:hover {
  background-color: #f5f5f5;
}
.small-text {
  font-size: 10px !important;
}
.bg-light {
  background-color: #f8f9fa;
}
</style>
